import React, { useMemo } from "react";

import Dropdown from "@/components/molecules/dropdown/Dropdown";
import FormControl from "@/components/molecules/form/FormControl";

type FormDropdownProps<T> = {
  name: string;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  options: T[];
  displayValue?: (option: T) => React.ReactNode;
  placeholder?: string | React.ReactNode;
  multiple?: boolean;
  renderOption?: (option: T) => React.ReactNode;
  renderPlaceholder?: React.ReactNode;
  hasError?: boolean;
  leftPlaceholder?: React.ReactNode | string;
  value: string | string[];
  onChange: (value: string | string[]) => void;
};

const FormDropdown = <T extends { id: string; name: string }>({
  name,
  label,
  helperText,
  disabled = false,
  options,
  displayValue = (option) => option?.name,
  placeholder,
  multiple = false,
  renderOption,
  renderPlaceholder,
  hasError = false,
  leftPlaceholder,
  value,
  onChange,
}: FormDropdownProps<T>) => {
  // Map the value (string or array of strings) to option object(s)
  const selected = useMemo(() => {
    if (multiple && Array.isArray(value)) {
      return options.filter((option) => value.includes(option.id));
    } else {
      return options.find((option) => option.id === value) || null;
    }
  }, [value, options, multiple]);

  const handleSelect = (selectedOption: T | T[] | null) => {
    if (multiple) {
      const selectedIds = Array.isArray(selectedOption) ? selectedOption.map((opt) => opt.id) : [];
      onChange(selectedIds);
    } else {
      const selectedId = selectedOption ? (selectedOption as T).id : "";
      onChange(selectedId);
    }
  };

  return (
    <FormControl label={label} id={name} helperText={helperText}>
      <Dropdown
        id={name}
        multiple={multiple}
        onSelect={handleSelect}
        options={options}
        selected={selected}
        leftPlaceholder={leftPlaceholder}
        hasError={hasError}
        renderPlaceholder={renderPlaceholder}
        disabled={disabled}
        placeholder={placeholder}
        displayValue={displayValue}
        renderOption={renderOption}
      />
    </FormControl>
  );
};
export default FormDropdown;
